import { PHASE } from "../../../common/constants.ts";
import { idb } from "../db/index.ts";
import getPayroll from "../core/team/getPayroll.ts";
import g from "./g.ts";
import local from "./local.ts";
import type {
	OwnerExpectations,
	OwnerFeedback,
} from "../../../common/types.ts";

// N4-3 in-season owner feedback letters. Turns the year-end owner evaluation
// (genMessage.ts) + season-start expectation "contract" (genExpectationMessage.ts)
// into ongoing mid-season tension delivered through the inbox — the only new
// pipeline (no overlay / toast). Three letter types:
//   1. Monthly pulse — record vs the owner's win target, only sent when the
//      progress verdict changes vs the previous pulse letter.
//   2. Milestone — mathematically clinched a playoff berth / mathematically
//      eliminated, each once per season.
//   3. Free-agency recap — a roster + cap-space snapshot when the free-agency
//      window closes.
// All guards mirror genMessage.ts (no auto-play / spectator / multi-team) and
// every type is idempotent (season + kind idempotency key stored on the
// message's `feedback` field). English source text is stored in the DB and
// reverse-translated by messageTranslations.ts, same as the other owner letters.

const FEEDBACK_FROM = "The Owner";

// A shared guard for all three letter types.
const feedbackDisabled = () =>
	local.autoPlayUntil ||
	g.get("spectator") ||
	g.get("userTids").length > 1;

const getSeasonExpectations = async (
	season: number,
): Promise<OwnerExpectations | undefined> => {
	const messages = await idb.getCopies.messages({}, "noCopyCache");
	return messages.find((message) => message.expectations?.season === season)
		?.expectations;
};

// ---------------------------------------------------------------------------
// 1. Monthly pulse
// ---------------------------------------------------------------------------

export type PulseStatus = "ahead" | "onTrack" | "behind";

export const PULSE_SUBJECT = "Season update";

// Pure verdict: project the current pace out to a full season and compare it to
// the owner's win target. The threshold scales with season length so it isn't
// hair-triggered on short/long schedules.
export const getPulseStatus = (input: {
	won: number;
	gamesPlayed: number;
	numGames: number;
	winTarget: number;
}): PulseStatus => {
	const { won, gamesPlayed, numGames, winTarget } = input;

	const projectedWins =
		gamesPlayed > 0 ? Math.round((won / gamesPlayed) * numGames) : 0;
	const threshold = Math.max(2, Math.round(numGames * 0.03));
	const diff = projectedWins - winTarget;

	if (diff >= threshold) {
		return "ahead";
	}
	if (diff <= -threshold) {
		return "behind";
	}
	return "onTrack";
};

const PULSE_VERDICT: Record<PulseStatus, string> = {
	ahead: "You're ahead of pace — keep it up.",
	onTrack: "You're right on pace. Stay the course.",
	behind: "You're behind pace. I need to see a turnaround.",
};

export const buildPulseText = (input: {
	won: number;
	lost: number;
	gamesPlayed: number;
	winTarget: number;
	status: PulseStatus;
}): string => {
	const { won, lost, gamesPlayed, winTarget, status } = input;

	const paragraphs = [
		`Through ${gamesPlayed} games, you're ${won}-${lost}.`,
		`Target: ${winTarget} wins.`,
		PULSE_VERDICT[status],
	];

	return paragraphs.map((p) => `<p>${p}</p>`).join("");
};

// The season is split into ~6 "months"; each checkpoint the pulse is evaluated
// at most once, and only fires when the verdict differs from the last pulse.
const getPulseCheckpointSize = (numGames: number) =>
	Math.max(1, Math.round(numGames / 6));

export const genPulseMessage = async () => {
	if (feedbackDisabled() || g.get("phase") !== PHASE.REGULAR_SEASON) {
		return;
	}

	const season = g.get("season");
	const userTid = g.get("userTid");
	const numGames = g.get("numGames");

	const teamSeason = await idb.cache.teamSeasons.indexGet(
		"teamSeasonsBySeasonTid",
		[season, userTid],
	);
	if (!teamSeason) {
		return;
	}

	const won = teamSeason.won;
	const lost = teamSeason.lost;
	const gamesPlayed =
		won + lost + (teamSeason.otl ?? 0) + (teamSeason.tied ?? 0);

	const checkpointSize = getPulseCheckpointSize(numGames);
	const currentCheckpoint = Math.floor(gamesPlayed / checkpointSize);

	// Never send in the first "month" — too early to judge, and it would fire on
	// game 1.
	if (currentCheckpoint < 1) {
		return;
	}

	const expectations = await getSeasonExpectations(season);
	// Prefer the owner's stated target; fall back to a .500 target for saves that
	// predate the expectation letter.
	const winTarget = expectations?.winTarget ?? Math.round(numGames * 0.5);

	const messages = await idb.getCopies.messages({}, "noCopyCache");
	const priorPulses = messages
		.filter(
			(message) =>
				message.feedback?.kind === "pulse" &&
				message.feedback.season === season,
		)
		.map((message) => message.feedback as OwnerFeedback);

	// At most one pulse per checkpoint.
	if (priorPulses.some((f) => f.pulseCheckpoint === currentCheckpoint)) {
		return;
	}

	const lastPulse = priorPulses.reduce<OwnerFeedback | undefined>(
		(latest, f) =>
			latest === undefined ||
			(f.pulseCheckpoint ?? 0) > (latest.pulseCheckpoint ?? 0)
				? f
				: latest,
		undefined,
	);

	const status = getPulseStatus({ won, gamesPlayed, numGames, winTarget });

	// Strict gate: only send when the verdict changed vs the last pulse letter.
	if (lastPulse?.pulseStatus === status) {
		return;
	}

	await idb.cache.messages.add({
		read: false,
		from: FEEDBACK_FROM,
		year: season,
		text: buildPulseText({ won, lost, gamesPlayed, winTarget, status }),
		subject: PULSE_SUBJECT,
		tid: userTid,
		feedback: {
			kind: "pulse",
			season,
			pulseStatus: status,
			pulseCheckpoint: currentCheckpoint,
		},
	});
};

// ---------------------------------------------------------------------------
// 2. Playoff milestones (clinched / eliminated)
// ---------------------------------------------------------------------------

export const MILESTONE_CLINCHED_SUBJECT = "Playoff berth clinched";
export const MILESTONE_ELIMINATED_SUBJECT = "Eliminated from playoff contention";

type PlayoffTarget = OwnerExpectations["playoffTarget"];

const CLINCHED_TAIL: Record<PlayoffTarget, string> = {
	contend:
		"That's the floor for a team with our talent — now go make a deep run.",
	makePlayoffs: "That's exactly what I asked for. Nicely done.",
	develop: "Ahead of schedule for a rebuilding year. Impressive.",
};

const ELIMINATED_TAIL: Record<PlayoffTarget, string> = {
	contend:
		"This is a real disappointment. We were built to contend, not to miss the playoffs.",
	makePlayoffs: "I expected us in the playoffs. We'll need to regroup.",
	develop:
		"I know this was a building year, so I won't overreact. Keep developing our young core.",
};

export const buildMilestoneText = (input: {
	kind: "clinched" | "eliminated";
	playoffTarget: PlayoffTarget | undefined;
}): string => {
	const { kind, playoffTarget } = input;

	const base =
		kind === "clinched"
			? "We've clinched a playoff spot."
			: "We've been eliminated from playoff contention.";

	const tail =
		playoffTarget === undefined
			? undefined
			: kind === "clinched"
				? CLINCHED_TAIL[playoffTarget]
				: ELIMINATED_TAIL[playoffTarget];

	const paragraphs = tail === undefined ? [base] : [base, tail];
	return paragraphs.map((p) => `<p>${p}</p>`).join("");
};

// Called from updateClinchedPlayoffs when the user team's clinched status flips.
// "w"/"x"/"y"/"z" all count as clinching a berth (first one wins); "o" is
// elimination. Each kind is idempotent per season, so only the first clinch and
// the elimination send a letter.
export const genMilestoneMessage = async (
	clinchedStatus: string | undefined,
) => {
	if (feedbackDisabled()) {
		return;
	}

	let kind: "clinched" | "eliminated";
	if (
		clinchedStatus === "w" ||
		clinchedStatus === "x" ||
		clinchedStatus === "y" ||
		clinchedStatus === "z"
	) {
		kind = "clinched";
	} else if (clinchedStatus === "o") {
		kind = "eliminated";
	} else {
		return;
	}

	const season = g.get("season");
	const userTid = g.get("userTid");

	const messages = await idb.getCopies.messages({}, "noCopyCache");
	if (
		messages.some(
			(message) =>
				message.feedback?.kind === kind &&
				message.feedback.season === season,
		)
	) {
		return;
	}

	const expectations = await getSeasonExpectations(season);

	await idb.cache.messages.add({
		read: false,
		from: FEEDBACK_FROM,
		year: season,
		text: buildMilestoneText({
			kind,
			playoffTarget: expectations?.playoffTarget,
		}),
		subject:
			kind === "clinched"
				? MILESTONE_CLINCHED_SUBJECT
				: MILESTONE_ELIMINATED_SUBJECT,
		tid: userTid,
		feedback: {
			kind,
			season,
		},
	});
};

// ---------------------------------------------------------------------------
// 3. Free-agency recap
// ---------------------------------------------------------------------------

export const FREE_AGENCY_SUMMARY_SUBJECT = "Free agency recap";

// Money values arrive in thousands of dollars; render as e.g. "$12.5M".
const formatMillions = (thousands: number) =>
	(Math.abs(thousands) / 1000).toFixed(1);

export const buildFreeAgencySummaryText = (input: {
	season: number;
	rosterCount: number;
	payroll: number;
	capSpace: number | undefined;
}): string => {
	const { season, rosterCount, payroll, capSpace } = input;

	const paragraphs = [
		`Free agency is closed. Here's where your roster stands for the ${season} season.`,
		`Roster: ${rosterCount} players signed.`,
		`Payroll: $${formatMillions(payroll)}M.`,
	];

	if (capSpace !== undefined) {
		paragraphs.push(
			capSpace >= 0
				? `Cap room: $${formatMillions(capSpace)}M below the cap.`
				: `Cap room: $${formatMillions(capSpace)}M over the cap.`,
		);
	}

	return paragraphs.map((p) => `<p>${p}</p>`).join("");
};

// Called when the free-agency window closes (start of the next preseason).
// Idempotent per season. Signed / lost player lists aren't reliably available at
// this hook, so per the lane brief this degrades to a roster + cap-space
// snapshot.
export const genFreeAgencySummaryMessage = async () => {
	if (feedbackDisabled()) {
		return;
	}

	const season = g.get("season");
	const userTid = g.get("userTid");

	const messages = await idb.getCopies.messages({}, "noCopyCache");
	if (
		messages.some(
			(message) =>
				message.feedback?.kind === "freeAgencySummary" &&
				message.feedback.season === season,
		)
	) {
		return;
	}

	const players = await idb.cache.players.indexGetAll("playersByTid", userTid);
	const rosterCount = players.length;

	const payroll = await getPayroll(userTid);
	const capSpace =
		g.get("salaryCapType") === "none"
			? undefined
			: g.get("salaryCap") - payroll;

	await idb.cache.messages.add({
		read: false,
		from: FEEDBACK_FROM,
		year: season,
		text: buildFreeAgencySummaryText({
			season,
			rosterCount,
			payroll,
			capSpace,
		}),
		subject: FREE_AGENCY_SUMMARY_SUBJECT,
		tid: userTid,
		feedback: {
			kind: "freeAgencySummary",
			season,
		},
	});
};
